import { createRequestAction } from '@fullstory/redux-util';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import {
  AlertsResponse,
  AlertNotificationsRequest,
  AlertNotificationsResponse,
  NewAlertRequest,
  AlertAndKey,
  AlertKey as ModelAlertKey,
  DeleteAlertRequest,
  UpdateAlertRequest,
  NotificationsResponse,
} from '@fullstory/client-api';
import {
  AlertKey as AmberAlertKey,
  FetchAlertDataRequest,
  FetchAlertDataResponse,
} from '@fullstory/proto/fs/services/amber';
import { AlertsTypes, NotificationsTypes } from './types';
import { RequestType } from '@fullstory/util/xhr';
import { int64, Timestamp } from '@fullstory/proto/google/protobuf';

const NEW_ALERT_ENDPOINT = '/alerts/new';
const GET_ALERTS_ENDPOINT = '/alerts';
const UPDATE_ALERT_ENDPOINT = '/alerts/update';
const DELETE_ALERT_ENDPOINT = '/alerts/delete';
const GET_ALERT_NOTIFICATIONS_ENDPOINT = '/alerts/notifications';
const GET_SERVER_NOTIFICATIONS_ENDPOINT = '/notifications';
const GET_ALERT_DATA_ENDPOINT = '/alertspb/FetchAlertData';

export const AlertsActions = {
  addAlert: (req: NewAlertRequest) =>
    createRequestAction<AlertAndKey>()(
      {
        type: AlertsTypes.AddAlertRequest,
        buildRequest: {
          requestType: RequestType.PostJson,
          url: NEW_ALERT_ENDPOINT,
        },
        body: req,
        readWrite: 'write',
      },
      AlertsTypes.AddAlertFailure,
      AlertsTypes.AddAlertSuccess,
    ),

  updateAlert: (req: UpdateAlertRequest) =>
    createRequestAction<AlertAndKey>()(
      {
        type: AlertsTypes.UpdateAlertRequest,
        buildRequest: {
          requestType: RequestType.PostJson,
          url: UPDATE_ALERT_ENDPOINT,
        },
        body: req,
        force: true,
        readWrite: 'write',
      },
      AlertsTypes.UpdateAlertFailure,
      AlertsTypes.UpdateAlertSuccess,
    ),

  deleteAlert: (req: DeleteAlertRequest) =>
    createRequestAction<AlertAndKey>()(
      {
        type: AlertsTypes.DeleteAlertRequest,
        buildRequest: {
          requestType: RequestType.PostJson,
          url: DELETE_ALERT_ENDPOINT,
        },
        body: req,
        readWrite: 'write',
      },
      AlertsTypes.DeleteAlertFailure,
      AlertsTypes.DeleteAlertSuccess,
    ),
};

const getAlertRequestAction =
  (request: string, success: string, failure: string) =>
  (segmentId?: string, searchieFlag?: Flag, skipCache?: boolean) =>
    createRequestAction<AlertsResponse>()(
      {
        type: request,
        buildRequest: {
          requestType: RequestType.PostJson,
          url: GET_ALERTS_ENDPOINT,
        },
        readWrite: 'write',
        body: {
          SegmentId: segmentId,
          SearchieFlag: searchieFlag,
        },
        force: skipCache ?? false,
      },
      success,
      failure,
    );

export const AlertsRequests = {
  getAlerts: getAlertRequestAction(
    AlertsTypes.FetchAlertsRequest,
    AlertsTypes.FetchAlertsSuccess,
    AlertsTypes.FetchAlertsFailure,
  ),
  // seperate overlay action to store in different reducer
  getOverlayAlerts: getAlertRequestAction(
    AlertsTypes.OverlayFetchAlertsRequest,
    AlertsTypes.OverlayFetchAlertsSuccess,
    AlertsTypes.OverlayFetchAlertsFailure,
  ),
  getAlertData: (alertKey: ModelAlertKey, start: Date, end: Date) => {
    const amberAlertKey = <AmberAlertKey>{
      seatName: alertKey.SeatName,
      searchieName: alertKey.SearchieName,
      alertId: alertKey.AlertId,
    };

    // When segmentId is 0, the segment is a canned segment and uses segmentName as the identifier
    if (alertKey.SegmentId !== '0') {
      amberAlertKey.segmentId = alertKey.SegmentId as int64;
    } else {
      amberAlertKey.segmentName = alertKey.SegmentName;
    }

    const body: FetchAlertDataRequest = {
      alertKey: amberAlertKey,
      startPoint: Timestamp.fromDate(start),
      endPoint: Timestamp.fromDate(end),
    };

    return createRequestAction<FetchAlertDataResponse>()(
      {
        type: AlertsTypes.FetchAlertDataRequest,
        buildRequest: {
          requestType: RequestType.PostJson,
          url: GET_ALERT_DATA_ENDPOINT,
        },
        body,
        readWrite: 'write',
      },
      AlertsTypes.FetchAlertDataSuccess,
      AlertsTypes.FetchAlertDataFailure,
    );
  },
};

export const NotificationsRequests = {
  getServerNotifications: () =>
    createRequestAction<NotificationsResponse>()(
      {
        type: NotificationsTypes.FetchServerNotificationsRequest,
        buildRequest: {
          requestType: RequestType.GetJson,
          url: GET_SERVER_NOTIFICATIONS_ENDPOINT,
        },
        readWrite: 'read',
      },
      NotificationsTypes.FetchServerNotificationsSuccess,
      NotificationsTypes.FetchServerNotificationsFailure,
    ),
  getNotifications: (body: AlertNotificationsRequest) =>
    createRequestAction<AlertNotificationsResponse>()(
      {
        type: NotificationsTypes.FetchAlertNotificationsRequest,
        buildRequest: {
          requestType: RequestType.PostJson,
          url: GET_ALERT_NOTIFICATIONS_ENDPOINT,
        },
        body,
        readWrite: 'write',
      },
      NotificationsTypes.FetchAlertNotificationsSuccess,
      NotificationsTypes.FetchAlertNotificationsFailure,
    ),
  // seperate overlay action to store in different reducer
  getOverlayNotifications: (body: AlertNotificationsRequest) =>
    createRequestAction<AlertNotificationsResponse>()(
      {
        type: NotificationsTypes.OverlayFetchAlertNotificationsRequest,
        buildRequest: {
          requestType: RequestType.PostJson,
          url: GET_ALERT_NOTIFICATIONS_ENDPOINT,
        },
        body,
        readWrite: 'write',
      },
      NotificationsTypes.OverlayFetchAlertNotificationsSuccess,
      NotificationsTypes.OverlayFetchAlertNotificationsFailure,
    ),
  updateNotificationsSeen: (time: number) =>
    createRequestAction()(
      {
        type: NotificationsTypes.NotificationsSeenRequest,
        buildRequest: {
          requestType: RequestType.PostJson,
          url: '/seat/updateNotificationsSeen',
        },
        body: time,
        readWrite: 'write',
      },
      NotificationsTypes.NotificationsSeenSuccess,
      NotificationsTypes.NotificationsSeenFailure,
    ),
};
